// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@awc/App/Components/Common/XngZone";
import XngCarousel from "@awc/App/Components/XngCarousel";
import getAlliedUniversityChildren from "@awc/App/Handlers/Content/getAlliedUniversityChildren";
import { CustomAppState } from "@awc/App/Store/Reducers";
import LazyImage, { LazyImageProps } from "@awc/MobiusOverrides/LazyImage";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import { xParserOptions } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { PageModel } from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps extends WidgetProps {
    fields: {
        faqPage: boolean;
    };
}
interface AlliedUniversityArticleViewStyles {
    container?: InjectableCss;
    articlesContainer?: InjectableCss;
    articleContainer?: InjectableCss;
    articleImage?: LazyImageProps;
    articleTitle?: LinkPresentationProps;
    articleInfo?: InjectableCss;
    articleQuickSummaryDisplay?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomAppState) => {
    const currentPageId = getCurrentPage(state).nodeId;
    const alliedUniversityPageId = getPageLinkByPageType(state, "AlliedUniversityPage")?.id || "";
    const alliedUniPageContent = state.xPagesContent[alliedUniversityPageId];
    return {
        currentPageId,
        isLoading: alliedUniPageContent?.isLoading,
        articles: alliedUniPageContent?.data,
        alliedUniversityPageId,
        currentPageInfo: alliedUniPageContent?.articlesPagesMap[currentPageId],
        tagMap: alliedUniPageContent?.tagMap,
        articlesPagesMap: alliedUniPageContent?.articlesPagesMap,
    };
};
const mapDispatchToProps = {
    getAlliedUniversityChildren,
};

export const alliedUniversityArticleViewStyles: AlliedUniversityArticleViewStyles = {};

const styles = alliedUniversityArticleViewStyles;

const AlliedUniversityArticleView: FC<Props> = ({
    isLoading,
    articles,
    tagMap,
    currentPageInfo,
    alliedUniversityPageId,
    getAlliedUniversityChildren,
    articlesPagesMap,
    currentPageId,
    id,
    fields,
}) => {
    const [relatedArticles, setRelatedArticles] = useState<PageModel[]>([]);
    const scriptTagRef = useRef<HTMLScriptElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!articles && !isLoading && !currentPageInfo && alliedUniversityPageId) {
            getAlliedUniversityChildren({ parentId: alliedUniversityPageId });
        }
    }, [alliedUniversityPageId]);

    // update related articles if current page changes
    useEffect(() => {
        if (!currentPageInfo) {
            return;
        }
        const currentPageTags = currentPageInfo.generalFields?.articleTags?.map(
            (tagItem: { fields: { tag: any } }) => tagItem.fields.tag,
        );
        const matchedPages = new Set<PageModel>();
        for (const tag of currentPageTags) {
            const pagesForTag = tagMap.get(tag);
            // exclude  current page from related articles
            if (pagesForTag) {
                pagesForTag.forEach(pageId => pageId !== currentPageId && matchedPages.add(articlesPagesMap[pageId]));
            }
        }
        const result = Array.from(matchedPages);
        setRelatedArticles(result);
    }, [tagMap, currentPageInfo]);

    // XNG-Changes
    useEffect(() => {
        if (!!currentPageInfo?.generalFields?.articleContents && fields.faqPage) {
            if (!containerRef.current) {
                return;
            }

            const questions = containerRef.current.querySelectorAll("h3");
            const schema: any = {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: [],
            };

            questions?.forEach(q => {
                const answer = q.nextElementSibling;
                if (answer && answer.tagName.toLowerCase() === "p") {
                    schema.mainEntity.push({
                        "@type": "Question",
                        name: q.innerText.trim(),
                        acceptedAnswer: {
                            "@type": "Answer",
                            text: (answer as HTMLElement).innerText.trim(),
                        },
                    });
                }
            });

            if (schema.mainEntity.length > 0) {
                scriptTagRef.current = document.createElement("script");
                scriptTagRef.current.type = "application/ld+json";
                scriptTagRef.current.text = JSON.stringify(schema);
                document.head.appendChild(scriptTagRef.current);
            }
        }

        return () => {
            if (scriptTagRef.current) {
                scriptTagRef.current.remove();
                scriptTagRef.current = null;
            }
        };
    }, [currentPageInfo?.generalFields?.articleContents, fields.faqPage]);

    return (
        <StyledWrapper {...styles.container} ref={containerRef}>
            <XngZone zoneName="titleImage" hint="Title Image" contentId={id} />
            {!!currentPageInfo?.generalFields?.articleContents && (
                <StyledWrapper>{parse(currentPageInfo.generalFields.articleContents, xParserOptions)}</StyledWrapper>
            )}
            <LoadingOverlay loading={isLoading}>
                {!!relatedArticles?.length && (
                    <>
                        <Typography {...styles.title}>{translate("Related Articles")}</Typography>
                        <StyledWrapper {...styles.articlesContainer}>
                            <XngCarousel spaceBetween={18} draggable={false} centerInsufficientSlides={false}>
                                {relatedArticles?.map(article => {
                                    const quickSummaryDisplay = article?.generalFields?.summary
                                        ? `${article?.generalFields?.summary.substring(0, 60)}...`
                                        : `${article?.generalFields?.articleContents?.substring(0, 60)}...`;

                                    return (
                                        <StyledWrapper key={article.id} {...styles.articleContainer}>
                                            <LazyImage
                                                src={article?.generalFields?.articleImage}
                                                altText={article?.name}
                                                {...styles.articleImage}
                                            />
                                            <StyledWrapper {...styles.articleInfo}>
                                                <Link href={article.generalFields?.url} {...styles.articleTitle}>
                                                    {article.name}
                                                </Link>

                                                {!!quickSummaryDisplay && (
                                                    <Typography {...styles.articleQuickSummaryDisplay}>
                                                        {parse(quickSummaryDisplay)}
                                                    </Typography>
                                                )}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    );
                                })}
                            </XngCarousel>
                        </StyledWrapper>
                    </>
                )}
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AlliedUniversityArticleView),
    definition: {
        displayName: "Allied University Article View",
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                name: "faqPage",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
            },
        ],
    },
};

export default widgetModule;
